import Phaser from "phaser";
import { installLetterboxFit } from "../ui/responsive";
import { ACHIEVEMENT_IDS, getOrCreatePlayerId, loadPlayerMemory, loadSettings } from "../../ai/localMemory";
import type { AchievementId, PlayerMemory } from "../../../../shared/types/PlayerMemory";
import { rivalryStageLabel } from "../../ui/rivalry";
import { buildCareerShareText, dailyDeepLink, shareOrCopy } from "../../ui/shareResult";
import { t, type Language } from "../../i18n";
import mapConfig from "../data/mapConfig.json";

/**
 * Read-only career dashboard: surfaces the stats the game already tracks (W/L, favorites,
 * lanes, veterans, rivalry) plus the unlockable achievements. Modeled on ReplayScene.
 */
export class CareerScene extends Phaser.Scene {
  private language: Language = "ko";

  constructor() {
    super("CareerScene");
  }

  create() {
    installLetterboxFit(this);
    this.language = loadSettings().language;
    const memory = loadPlayerMemory(getOrCreatePlayerId());

    this.add.image(640, 360, mapConfig.mapAssetKey).setDisplaySize(1280, 720);
    this.add.rectangle(640, 360, 1280, 720, 0x08070a, 0.62);
    this.add.rectangle(640, 360, 1080, 620, 0x11131b, 0.92).setStrokeStyle(2, 0xc9b878, 0.9);

    this.add.text(150, 64, t(this.language, "careerTitle"), titleStyle("#ffe58e", 28));
    this.add.text(150, 104, this.headlineLine(memory), titleStyle("#a7d8ff", 15));

    this.drawStats(memory);
    this.drawRivalry(memory);
    this.drawAchievements(memory);

    this.createButton(548, 656, t(this.language, "backToMenu"), () => this.scene.start("MenuScene"));
    this.createButton(732, 656, t(this.language, "share"), () => void this.shareCareer(memory));
    this.input.keyboard?.on("keydown-ESC", () => this.scene.start("MenuScene"));
  }

  /** Share the player's rivalry record (Web Share API, clipboard fallback). */
  private async shareCareer(memory: PlayerMemory) {
    const text = buildCareerShareText(this.language, {
      rivalryStageLabel: rivalryStageLabel(memory.rivalryStage, this.language),
      nickname: memory.nickname,
      level: memory.commanderLevel
    });
    const outcome = await shareOrCopy({ title: "Duel Siege AI", text, url: dailyDeepLink() });
    if (outcome === "failed") return;
    const label = this.add
      .text(640, 612, outcome === "copied" ? t(this.language, "linkCopied") : t(this.language, "shared"), titleStyle("#9ff0b0", 16))
      .setOrigin(0.5)
      .setDepth(220);
    this.tweens.add({ targets: label, y: 590, alpha: 0, duration: 1500, delay: 500, onComplete: () => label.destroy() });
  }

  private headlineLine(memory: PlayerMemory): string {
    const total = memory.battleCount;
    const rate = total > 0 ? Math.round((memory.wins / total) * 100) : 0;
    return `${t(this.language, "commanderLevel")} ${memory.commanderLevel}  ·  ${t(this.language, "warFunds")} ${memory.warFunds}  ·  ${t(this.language, "winRate")} ${rate}%`;
  }

  private drawStats(memory: PlayerMemory) {
    const x = 150;
    let y = 158;
    const total = memory.battleCount;
    const rate = total > 0 ? Math.round((memory.wins / total) * 100) : 0;
    const rows: Array<[string, string]> = [
      [t(this.language, "battles"), `${total}  ( ${memory.wins}W / ${memory.losses}L · ${rate}% )`],
      [t(this.language, "avgSurvival"), `${memory.averageSurvivalTime}s`],
      [t(this.language, "favoriteTowers"), this.listOrDash(memory.favoriteTowers.slice(0, 3))],
      [t(this.language, "favoriteUnits"), this.listOrDash(memory.favoriteUnits.slice(0, 3))],
      [t(this.language, "strongLanes"), this.laneList(memory.strongLanes)],
      [t(this.language, "weakLanes"), this.laneList(memory.weakLanes)]
    ];
    if ((memory.bestEndlessTime ?? 0) > 0) {
      rows.push([t(this.language, "bestEndless"), `${memory.bestEndlessTime}s`]);
    }
    this.add.text(x, y - 24, "📊", titleStyle("#ffffff", 16));
    rows.forEach(([label, value]) => {
      this.add.text(x, y, label, titleStyle("#c9b878", 14));
      this.add.text(x + 168, y, value, { ...titleStyle("#eef2ff", 14), wordWrap: { width: 300 } });
      y += 34;
    });

    // Veteran roster
    y += 6;
    this.add.text(x, y, t(this.language, "veteranRoster"), titleStyle("#c9b878", 14));
    y += 26;
    const vets = memory.veterans.slice(0, 4);
    if (vets.length === 0) {
      this.add.text(x, y, t(this.language, "noVeterans"), titleStyle("#8d8678", 13));
    } else {
      vets.forEach((vet) => {
        this.add.text(x, y, `★ ${vet.nickname}  ·  ${vet.kills} kills  ·  ${vet.survivedBattles} battles`, titleStyle("#dfe9c2", 13));
        y += 24;
      });
    }
  }

  private drawRivalry(memory: PlayerMemory) {
    const x = 700;
    let y = 158;
    this.add.text(x, y - 24, t(this.language, "rivalryTimeline"), titleStyle("#ff9b73", 16));
    const stage = rivalryStageLabel(memory.rivalryStage, this.language);
    this.add.text(x, y, `${stage}  ·  ${memory.rivalryScore}${memory.nickname ? `  ·  ${memory.nickname}` : ""}`, { ...titleStyle("#ffd6a8", 14), wordWrap: { width: 360 } });
    y += 34;
    const history = memory.notableHistory.slice(0, 5);
    if (history.length === 0) {
      this.add.text(x, y, "—", titleStyle("#8d8678", 13));
    } else {
      history.forEach((entry) => {
        const mark = entry.result === "win" ? "▲" : "▼";
        const color = entry.result === "win" ? "#9be8ff" : "#ff9b73";
        this.add.text(x, y, `${mark} ${entry.note}`, { ...titleStyle(color, 12), wordWrap: { width: 360 } });
        y += 30;
      });
    }
  }

  private drawAchievements(memory: PlayerMemory) {
    const x = 700;
    const y0 = 396;
    this.add.text(x, y0 - 26, t(this.language, "achievements"), titleStyle("#c9b878", 16));
    const cols = 3;
    const cellW = 158;
    const cellH = 38;
    ACHIEVEMENT_IDS.forEach((id, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const cx = x + col * cellW;
      const cy = y0 + row * cellH;
      const unlocked = Boolean(memory.achievements?.[id]);
      this.add.text(cx, cy, `${unlocked ? "🏅" : "🔒"} ${t(this.language, achievementLabelKey(id))}`, titleStyle(unlocked ? "#ffe58e" : "#6f6a5c", 12));
    });
  }

  private listOrDash(values: string[]): string {
    return values.length > 0 ? values.join(", ") : "—";
  }

  private laneList(lanes: number[]): string {
    return lanes.length > 0 ? lanes.map((lane) => `${lane}`).join(", ") : "—";
  }

  private createButton(x: number, y: number, label: string, onClick: () => void) {
    const button = this.add.container(x, y).setDepth(200);
    const frame = this.add.rectangle(0, 0, 150, 40, 0x17364a, 0.96).setStrokeStyle(1, 0xc9b878, 0.9);
    const text = this.add.text(0, 0, label, titleStyle("#f7f2df", 15)).setOrigin(0.5);
    button.add([frame, text]);
    button.setSize(150, 40);
    button.setInteractive({ useHandCursor: true });
    button.on("pointerover", () => frame.setFillStyle(0x235372, 1));
    button.on("pointerout", () => frame.setFillStyle(0x17364a, 0.96));
    button.on("pointerdown", onClick);
  }
}

function achievementLabelKey(id: AchievementId) {
  return `ach_${id}` as const;
}

function titleStyle(color: string, fontSize: number): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
    fontSize: `${fontSize}px`,
    color,
    stroke: "#08070a",
    strokeThickness: 3
  };
}
